import { useCallback, useEffect, useRef, useState } from 'react';
import * as Sentry from '@sentry/react-native';
import { apiService } from '../services/apiService';
import { isTransientNetworkError, toError } from '../utils/errorUtils';
import logger from '../utils/logger';
import supabase from '../utils/supabaseClient';
import { useTier } from '../context/TierContext';

/**
 * useUnifiedListingsFeed - Hook for fetching and subscribing to the unified listings feed.
 *
 * Supports cursor-based pagination, model filtering, favorites mode,
 * Realtime subscription with debounced refetch, and optimistic save/unsave/dismiss.
 *
 * @param {Object} session - The authenticated user session
 * @param {Object} [options={}] - Feed options
 * @param {boolean} [options.favoritesOnly=false] - Only show saved listings
 * @param {string|null} [options.modelId=null] - Filter by model UUID
 * @returns {{
 *   listings: Array,
 *   isLoading: boolean,
 *   isEmpty: boolean,
 *   totalCount: number,
 *   models: Array,
 *   fetchNextPage: Function,
 *   hasNextPage: boolean,
 *   isFetchingNextPage: boolean,
 *   saveListing: Function,
 *   unsaveListing: Function,
 *   dismissListing: Function,
 *   refetch: Function,
 * }}
 */
export const useUnifiedListingsFeed = (session, options = {}) => {
  const { favoritesOnly = false, modelId = null, sourceFilter = null, displayCap = null } = options;
  // Effective page size: when a displayCap is set (e.g. Free 10-listing cap), we
  // request that exact number from the server and disable pagination.
  const pageLimit = displayCap != null ? displayCap : 20;

  const [listings, setListings] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  // True when the initial feed load failed (vs. a genuinely empty feed). Lets
  // the screen show a retry affordance instead of a misleading "no listings".
  const [loadError, setLoadError] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  // Global "listings scanned today" count from the server (feed freshness bar).
  // null until the first page resolves so the bar can show a placeholder.
  const [scannedToday, setScannedToday] = useState(null);
  const [models, setModels] = useState([]);
  const [nextCursor, setNextCursor] = useState(null);
  const [isFetchingNextPage, setIsFetchingNextPage] = useState(false);
  // Counted independently of the active filter so the My links pill shows
  // whenever the user has external_url listings, even when a Free display cap
  // or a per-model filter would truncate them out of the main feed list.
  const [totalExternalLinkCount, setTotalExternalLinkCount] = useState(0);
  // Server-side feed cap signal (monetization-step-6 Phase F task 25). The
  // edge function returns cap_applied/cap/tier in the payload; surfacing
  // these lets FeedCapCTA and similar surfaces use the authoritative
  // server flag instead of client-side tier inference.
  const [feedCapApplied, setFeedCapApplied] = useState(false);
  const [feedCapValue, setFeedCapValue] = useState(null);
  const [feedCapTier, setFeedCapTier] = useState(null);

  const userId = session?.user?.id;
  const sessionRef = useRef(session);
  sessionRef.current = session;

  const { hasProAccess } = useTier();
  const staleCheckFiredRef = useRef(false);

  // On initial mount for Free users, trigger server-side staleness check.
  // The `trigger-listing-feed` edge function compares feed_generated_at to a
  // >24h threshold and enqueues a `generate_listing_feed` job if stale.
  // Handles users returning after 14+ day inactivity whose Sunday regen is old.
  useEffect(() => {
    if (!userId || hasProAccess || staleCheckFiredRef.current) return;
    staleCheckFiredRef.current = true;
    apiService.invokeEdgeFunction('trigger-listing-feed').catch((err) => {
      logger.error('[useUnifiedListingsFeed] trigger-listing-feed failed (non-blocking):', err);
    });
  }, [userId, hasProAccess]);

  // Count external_url listings independently of the active filter/cap so the
  // My links pill shows whenever the user has any. Runs on mount and whenever
  // the user changes. Cheap: head-count with `select(*, { count: 'exact' })`.
  useEffect(() => {
    if (!userId) {
      setTotalExternalLinkCount(0);
      return;
    }
    let cancelled = false;
    supabase
      .from('user_listing_feeds')
      .select('id', { count: 'exact', head: true })
      .eq('user_id', userId)
      .eq('source', 'external_url')
      .then(({ count, error }) => {
        if (cancelled) return;
        if (error) {
          logger.error('[useUnifiedListingsFeed] external link count failed:', error);
          setTotalExternalLinkCount(0);
          return;
        }
        setTotalExternalLinkCount(count ?? 0);
      });
    return () => { cancelled = true; };
  }, [userId]);

  const channelRef = useRef(null);
  const debounceTimerRef = useRef(null);
  const nextCursorRef = useRef(nextCursor);
  nextCursorRef.current = nextCursor;
  const isFetchingRef = useRef(isFetchingNextPage);
  isFetchingRef.current = isFetchingNextPage;
  // Track whether user has paginated beyond page 1.
  // Realtime refetch resets to page 1, which would clobber paginated data.
  const hasPaginatedRef = useRef(false);

  /**
   * Fetch the initial page of the feed.
   */
  const fetchInitial = useCallback(async () => {
    if (!userId) return;

    setIsLoading(true);
    setListings([]);
    setLoadError(false);
    try {
      const response = await apiService.getUnifiedFeed({
        model_id: modelId,
        favorites_only: favoritesOnly,
        source_filter: sourceFilter,
        limit: pageLimit,
      });

      setListings(response.listings || []);
      setTotalCount(response.totalCount || 0);
      if (response.scannedToday !== undefined) {
        setScannedToday(response.scannedToday);
      }
      setFeedCapApplied(!!response.cap_applied);
      setFeedCapValue(response.cap ?? null);
      setFeedCapTier(response.tier ?? null);
      // Only update models list on unfiltered fetch to preserve full pill bar
      if (!modelId) {
        // Merge feed models with all selected_for_feed models from fit_scores
        // This ensures models with 0 listings still appear in the pill bar
        const feedModels = response.models || [];
        try {
          const { data: allSelected } = await supabase
            .from('user_model_fit_scores')
            .select('model_id, vehicle_models!inner(make, model)')
            .eq('user_id', userId)
            .eq('selected_for_feed', true)
            .is('deleted_at', null);

          if (allSelected && allSelected.length > 0) {
            const feedModelIds = new Set(feedModels.map(m => m.model_id));
            const missing = allSelected
              .filter(s => !feedModelIds.has(s.model_id))
              .map(s => ({
                model_id: s.model_id,
                make: s.vehicle_models.make,
                model: s.vehicle_models.model,
                source: 'user_added',
              }));
            setModels([...feedModels, ...missing]);
          } else {
            setModels(feedModels);
          }
        } catch (mergeErr) {
          logger.error('[useUnifiedListingsFeed] Error merging models:', mergeErr);
          setModels(feedModels);
        }
      }
      // When displayCap is active, disable pagination regardless of server cursor
      setNextCursor(displayCap != null ? null : (response.nextCursor || null));
      hasPaginatedRef.current = false;
    } catch (err) {
      logger.error('[useUnifiedListingsFeed] Error fetching feed:', err);
      setLoadError(true);
      // Transient mobile network failures (apiService already retried once)
      // are surfaced as a retry affordance, not a Sentry hard error.
      if (!isTransientNetworkError(err)) {
        Sentry.captureException(toError(err), {
          tags: { hook: 'useUnifiedListingsFeed', operation: 'fetchInitial' },
          contexts: { feed: { user_id: userId, model_id: modelId, favoritesOnly } },
        });
      }
    } finally {
      setIsLoading(false);
    }
  }, [userId, modelId, favoritesOnly, sourceFilter, pageLimit, displayCap]);

  /**
   * Fetch the next page using cursor pagination.
   */
  const fetchNextPage = useCallback(async () => {
    // displayCap disables pagination entirely
    if (displayCap != null) return;
    const cursor = nextCursorRef.current;
    if (!cursor || isFetchingRef.current || !userId) return;

    setIsFetchingNextPage(true);
    isFetchingRef.current = true;
    try {
      const response = await apiService.getUnifiedFeed({
        model_id: modelId,
        favorites_only: favoritesOnly,
        source_filter: sourceFilter,
        cursor,
        limit: pageLimit,
      });

      const newListings = response.listings || [];
      setListings((prev) => [...prev, ...newListings]);
      setNextCursor(response.nextCursor || null);
      nextCursorRef.current = response.nextCursor || null;
      hasPaginatedRef.current = true;
    } catch (err) {
      logger.error('[useUnifiedListingsFeed] Error fetching next page:', err);
      Sentry.captureException(err, {
        tags: { hook: 'useUnifiedListingsFeed', operation: 'fetchNextPage' },
      });
    } finally {
      setIsFetchingNextPage(false);
      isFetchingRef.current = false;
    }
  }, [userId, modelId, favoritesOnly, sourceFilter, pageLimit, displayCap]);

  /**
   * Public refetch — reloads from the beginning (preserves scroll position intent
   * by replacing data in-place rather than clearing first).
   */
  const refetch = useCallback(async () => {
    if (!userId) return;

    try {
      const response = await apiService.getUnifiedFeed({
        model_id: modelId,
        favorites_only: favoritesOnly,
        source_filter: sourceFilter,
        limit: pageLimit,
      });

      setListings(response.listings || []);
      setTotalCount(response.totalCount || 0);
      if (response.scannedToday !== undefined) {
        setScannedToday(response.scannedToday);
      }
      setFeedCapApplied(!!response.cap_applied);
      setFeedCapValue(response.cap ?? null);
      setFeedCapTier(response.tier ?? null);
      // Only update models list on unfiltered fetch to preserve full pill bar
      if (!modelId) {
        setModels(response.models || []);
      }
      setNextCursor(displayCap != null ? null : (response.nextCursor || null));
      setLoadError(false);
    } catch (err) {
      logger.error('[useUnifiedListingsFeed] Error refetching:', err);
      setLoadError(true);
      if (!isTransientNetworkError(err)) {
        Sentry.captureException(toError(err), {
          tags: { hook: 'useUnifiedListingsFeed', operation: 'refetch' },
        });
      }
    }
  }, [userId, modelId, favoritesOnly, sourceFilter, pageLimit, displayCap]);

  /**
   * Optimistic save — updates local state immediately, calls API in background.
   */
  const saveListing = useCallback(async (feedEntryId) => {
    // Optimistic update
    setListings((prev) =>
      prev.map((l) =>
        l.feed_entry_id === feedEntryId ? { ...l, saved_at: new Date().toISOString() } : l
      )
    );

    try {
      await apiService.recordListingInteraction(feedEntryId, 'save');
    } catch (err) {
      // Rollback
      setListings((prev) =>
        prev.map((l) =>
          l.feed_entry_id === feedEntryId ? { ...l, saved_at: null } : l
        )
      );
      logger.error('[useUnifiedListingsFeed] Save failed:', err);
    }
  }, []);

  /**
   * Optimistic unsave — clears saved_at locally, calls API in background.
   */
  const unsaveListing = useCallback(async (feedEntryId) => {
    // Capture previous value for rollback
    let previousSavedAt = null;
    setListings((prev) =>
      prev.map((l) => {
        if (l.feed_entry_id === feedEntryId) {
          previousSavedAt = l.saved_at;
          return { ...l, saved_at: null };
        }
        return l;
      })
    );

    try {
      await apiService.recordListingInteraction(feedEntryId, 'unsave');
    } catch (err) {
      // Rollback
      setListings((prev) =>
        prev.map((l) =>
          l.feed_entry_id === feedEntryId ? { ...l, saved_at: previousSavedAt } : l
        )
      );
      logger.error('[useUnifiedListingsFeed] Unsave failed:', err);
    }
  }, []);

  /**
   * Optimistic dismiss — removes from local list immediately.
   */
  const dismissListing = useCallback(async (feedEntryId) => {
    // Optimistic removal
    setListings((prev) => prev.filter((l) => l.feed_entry_id !== feedEntryId));
    setTotalCount((prev) => Math.max(0, prev - 1));

    try {
      await apiService.recordListingInteraction(feedEntryId, 'dismiss');
    } catch (err) {
      // On failure, refetch to restore correct state
      logger.error('[useUnifiedListingsFeed] Dismiss failed, refetching:', err);
      refetch();
    }
  }, [refetch]);

  /**
   * Initial fetch on mount or when filter params change.
   */
  useEffect(() => {
    if (!userId) {
      setListings([]);
      setIsLoading(false);
      setTotalCount(0);
      setModels([]);
      setNextCursor(null);
      return;
    }

    fetchInitial();
  }, [userId, fetchInitial]);

  /**
   * Realtime subscription — debounced refetch on any user_listing_feeds change.
   */
  useEffect(() => {
    if (!userId) return;

    const channelName = `unified_feed:${userId}`;

    // Clean up existing channel
    const existingChannels = supabase.getChannels();
    const existing = existingChannels.find((ch) => ch.topic === `realtime:${channelName}`);
    if (existing) {
      supabase.removeChannel(existing);
    }

    const channel = supabase
      .channel(channelName)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'user_listing_feeds',
          filter: `user_id=eq.${userId}`,
        },
        () => {
          // Skip Realtime refetch if user has paginated — refetch resets to
          // page 1 which would clobber the loaded pages.
          if (hasPaginatedRef.current || isFetchingRef.current) return;

          // Debounce refetch by 3s to let parallel feed generation + surfacing
          // settle before re-querying (avoids flickering counts mid-batch)
          if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current);
          }
          debounceTimerRef.current = setTimeout(() => {
            if (!hasPaginatedRef.current && !isFetchingRef.current) {
              refetch();
            }
          }, 3000);
        }
      )
      .subscribe((status, err) => {
        if (err) {
          logger.error('[useUnifiedListingsFeed] Realtime error:', err);
          Sentry.captureException(err, {
            tags: { hook: 'useUnifiedListingsFeed', operation: 'subscribe' },
          });
        }
        if (status === 'SUBSCRIBED') {
          logger.log(`[useUnifiedListingsFeed] Subscribed to Realtime for user: ${userId}`);
        }
      });

    channelRef.current = channel;

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = null;
      }
      if (channelRef.current) {
        supabase.removeChannel(channelRef.current);
        channelRef.current = null;
      }
    };
  }, [userId, refetch]);

  const isEmpty = !isLoading && listings.length === 0;
  // displayCap forces hasNextPage to false (no pagination past the cap)
  const hasNextPage = displayCap != null ? false : !!nextCursor;

  // Pill badge uses the user-wide count so it shows on every filter whenever
  // the user has any paste-a-link listings, not only when the owning model's
  // filter is active.
  const externalLinkCount = totalExternalLinkCount;

  return {
    listings,
    isLoading,
    isEmpty,
    loadError,
    totalCount,
    scannedToday,
    models,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    saveListing,
    unsaveListing,
    dismissListing,
    refetch,
    externalLinkCount,
    // Server-side feed cap signals (monetization-step-6 Phase F).
    feedCapApplied,
    feedCapValue,
    feedCapTier,
  };
};
